import { useEffect, useRef, useState } from 'react';
import { ApiError, listSales } from '../../lib/api';
import type { AdminSalesResponse, SalesEvent, SalesKind } from '../../lib/types';
import { formatLocal } from '../../lib/time';
import { PS, T } from '../../lib/theme';

const PAGE_SIZE = 25;

type FilterOption = { value: SalesKind | ''; label: string };

const FILTERS: FilterOption[] = [
  { value: '', label: 'All' },
  { value: 'booking', label: 'Booking' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'tournament', label: 'Tournament' },
];

function labelForKind(kind: SalesKind): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

function formatAmount(amountMinor: number): string {
  const sign = amountMinor < 0 ? '-' : '+';
  return `${sign}${(Math.abs(amountMinor) / 100).toFixed(2)} GEL`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return 'You are not authorized to view sales.';
  }
  return error instanceof Error && error.message
    ? error.message
    : 'Sales could not be loaded. Please try again.';
}

function hasUnavailableRefundHistory(history: AdminSalesResponse['history']): boolean {
  return !history.refund_history_complete
    && history.refund_events_available_from === null
    && history.issue_codes.includes('refund_operations_not_implemented');
}

function RefundAvailability({ history }: { history: AdminSalesResponse['history'] }) {
  if (!hasUnavailableRefundHistory(history)) return null;
  return (
    <p
      role="status"
      style={{
        margin: 0,
        padding: '10px 12px',
        border: `1px solid ${PS.infoBorder}`,
        background: PS.infoBg,
        color: PS.infoText,
        ...T.ui,
      }}
    >
      Refund operations are not implemented. Refund history is unavailable.
    </p>
  );
}

function MissingTimestampNotice({ history }: { history: AdminSalesResponse['history'] }) {
  if (!history.issue_codes.includes('sale_timestamp_missing')) return null;
  return (
    <p style={{ margin: 0, ...T.caption }}>
      Some sales are not shown because their settlement time is unavailable.
    </p>
  );
}

function EventRow({ event }: { event: SalesEvent }) {
  const isRefund = event.operation === 'refund';
  const missing = '—';
  return (
    <tr>
      <td style={cellStyle}>{formatLocal(event.occurred_at, 'd MMM yyyy, HH:mm')}</td>
      <td style={{ ...cellStyle, color: isRefund ? PS.danger : PS.success, ...T.bodyBold }}>
        {isRefund ? 'Refund' : 'Sale'}
      </td>
      <td style={cellStyle}>{labelForKind(event.kind)}</td>
      <td style={{ ...cellStyle, color: isRefund ? PS.danger : PS.success, ...T.bodyBold }}>
        {formatAmount(event.amount_minor)}
      </td>
      <td style={cellStyle}>
        <span>Payment #{event.payment_id}</span>
        <span style={{ color: PS.textSec }}> · </span>
        <span style={{ color: PS.textSec }}>{event.reference ?? missing}</span>
      </td>
      <td style={cellStyle}>{event.customer?.name ?? missing}</td>
    </tr>
  );
}

export function SalesPage() {
  const [kind, setKind] = useState<SalesKind | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<AdminSalesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const requestVersion = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    setResult(null);

    void listSales({ kind, limit: PAGE_SIZE, offset }, controller.signal)
      .then((next) => {
        if (requestVersion.current === version && !controller.signal.aborted) setResult(next);
      })
      .catch((nextError: unknown) => {
        if (requestVersion.current === version && !controller.signal.aborted) setError(errorMessage(nextError));
      })
      .finally(() => {
        if (requestVersion.current === version && !controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [kind, offset, retry]);

  const page = result ? Math.floor(result.offset / result.limit) + 1 : Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = result && result.total > 0 ? Math.ceil(result.total / result.limit) : 1;
  const filterLabel = kind ? labelForKind(kind).toLowerCase() : 'all';
  const canGoBack = offset > 0;
  const canGoForward = result !== null && offset + result.items.length < result.total;

  return (
    <section aria-labelledby="sales-heading" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1200 }}>
      <header>
        <h1 id="sales-heading" style={{ margin: 0, color: PS.nearBlack, ...T.sectionTitle }}>Sales</h1>
        <p style={{ margin: '6px 0 0', color: PS.textSec, ...T.ui }}>
          Settled club Flitt card sales, newest first.
        </p>
      </header>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: PS.nearBlack, ...T.ui }}>
        Sale type
        <select
          aria-label="Sale type"
          value={kind ?? ''}
          onChange={(event) => {
            const next = event.target.value as SalesKind | '';
            setKind(next || undefined);
            setOffset(0);
          }}
          style={{ minHeight: 40, border: `1px solid ${PS.border}`, borderRadius: 3, background: PS.white, color: PS.nearBlack, ...T.ui }}
        >
          {FILTERS.map((filter) => <option key={filter.value || 'all'} value={filter.value}>{filter.label}</option>)}
        </select>
      </label>

      {loading && <p role="status" style={{ margin: 0, color: PS.textSec, ...T.ui }}>Loading sales…</p>}

      {error && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: `1px solid ${PS.errorBorder}`, background: PS.errorBg, color: PS.errorText, ...T.ui }}>
          <span>{error}</span>
          <button type="button" onClick={() => setRetry((current) => current + 1)} style={secondaryButtonStyle}>Retry</button>
        </div>
      )}

      {result && (
        <>
          <RefundAvailability history={result.history} />
          <MissingTimestampNotice history={result.history} />
          {result.items.length === 0 ? (
            <p style={{ margin: 0, color: PS.textSec, ...T.ui }}>No {filterLabel === 'all' ? '' : `${filterLabel} `}sales found.</p>
          ) : (
            <div style={{ overflowX: 'auto', border: `1px solid ${PS.borderLight}`, background: PS.white }}>
              <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Date/time', 'Operation', 'Purchase type', 'Amount', 'Payment/reference', 'Customer'].map((heading) => (
                      <th key={heading} scope="col" style={headingStyle}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>{result.items.map((event) => <EventRow key={event.id} event={event} />)}</tbody>
              </table>
            </div>
          )}

          <nav aria-label="Sales pagination" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" disabled={!canGoBack} onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))} style={secondaryButtonStyle}>Previous page</button>
            <span style={{ color: PS.textSec, ...T.ui }}>Page {page} of {totalPages}</span>
            <button type="button" disabled={!canGoForward} onClick={() => setOffset((current) => current + PAGE_SIZE)} style={secondaryButtonStyle}>Next page</button>
          </nav>
        </>
      )}
    </section>
  );
}

const cellStyle = {
  padding: '12px 14px',
  borderBottom: `1px solid ${PS.borderLight}`,
  color: PS.nearBlack,
  textAlign: 'left' as const,
  verticalAlign: 'top' as const,
  ...T.ui,
};

const headingStyle = {
  ...cellStyle,
  background: PS.pageSecondary,
  ...T.label,
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: '8px 12px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  background: PS.white,
  color: PS.nearBlack,
  cursor: 'pointer',
  ...T.navLink,
};
